import {defineSlotRecipe} from '@chakra-ui/react3'
import {HEADER_FLOATING_ACTIVE_VAR} from './header-variant'
import {CTA_REVEAL_TRANSITION} from './partials/cta-reveal.recipe'

// How far the header sits below the top of the viewport, published on the header
// root. The `root` slot states the resting value; `index.jsx` writes the measured
// one over it while the flyout is open. Only `flyout/index.recipe.js` reads it.
export const HEADER_VIEWPORT_OFFSET_VAR = '--header-viewport-offset-block'

/**
 * Slot recipe for the Xleap navigation header.
 *
 * - `root` is the sticky positioner pinned to the top of the viewport. It
 *   publishes the `--app-header-offset-*` insets, which are breakpoint-driven,
 *   not variant-driven: the bar is flush and full-bleed on mobile and sits as a
 *   card inset by `spacing-20` from the `s` breakpoint up, in both variants. It
 *   stays transform-free in every state (so it
 *   never becomes the containing block for the fixed flyout backdrop).
 * - `cardContainer` applies those insets and, in the floating variant,
 *   collapses the header's reserved layout space via a negative `marginBlockEnd`.
 *   Its own block size is pinned to the closed header height
 *   (`--app-header-flow-block-size`), so the card overflows it while the flyout
 *   is open and the header's footprint in flow stays constant in both variants.
 * - `card` is the header's opaque surface — a full-bleed band inline, a rounded,
 *   shadowed pill when floating — wrapping the bar and the flyout in a box that
 *   clips its block axis. The clipping is what lets the flyout reveal (see
 *   `flyout/index.recipe.js`) grow from `0fr → 1fr` behind the bar; it is raised above
 *   the backdrop, and its opacity is what keeps the header undimmed over that
 *   backdrop while the nav is open. It also owns the auto-hide-on-scroll slide,
 *   keyed off the `data-hidden` attribute the component sets on the root from
 *   `useHideOnScroll`: the transform lives here rather than on the root because
 *   the root's own height is collapsed to ~0 by the container's negative margin
 *   when floating, and because the backdrop (a root child, outside the card)
 *   must stay anchored to the viewport.
 * - `bar` is the full-width padding wrapper that adds the page gutter back on top
 *   of the card inset (so its content edge stays aligned with the modules); it is
 *   raised (`zIndex: docked`) so the flyout panel slides out from behind it.
 * - `barInner` is the flex row, width-constrained via `layerStyle="container.page"`
 *   on the element (matching the `ModuleContainer` alignment pattern); the recipe
 *   owns only the flex layout.
 * - `nav` lays out the header links as a horizontal row. The regular header
 *   links and the "Products" main link are styled through the shared Link
 *   recipe (`app/leap/theme/recipes/link.js`) via the `HEADER_LINK_VARIANT`
 *   bridge map below, not through dedicated slots.
 * - `ctas` groups the desktop search, wishlist, account, cart, and mobile
 *   menu controls. On mobile, the search CTA replaces the logo in `barInner`.
 * - `menuButton` is the mobile-only flyout toggle: a separate DOM node shown on
 *   mobile and hidden on desktop via `display`, never conditionally mounted.
 * - `desktopCta` reveals wishlist and account in the open mobile CTA cluster
 *   as a cross-fade rather than an instant swap (STOK-17405). The search CTA
 *   owns its desktop and mobile presentation recipes.
 * - `focusTrap` is the `react-focus-lock` container that wraps the header's
 *   interactive content while the flyout is open. It is `display: contents` so
 *   it adds no box of its own — the sticky `root` stays the direct flow child
 *   of the app scroll container (a real wrapper here would confine the sticky
 *   header to its own height and break stickiness).
 */
export const headerRecipe = defineSlotRecipe({
    slots: [
        'root',
        'focusTrap',
        'cardContainer',
        'card',
        'bar',
        'barInner',
        'logoSearch',
        'logo',
        'logoIcon',
        'nav',
        'navLinkText',
        'divider',
        'ctas',
        'menuButton',
        'desktopCta',
        'mobileAccountCta'
    ],
    base: {
        root: {
            position: 'sticky',
            // Always at 0 - the spacing at the top is handled by the card container
            top: '0',
            zIndex: 'sticky',
            width: 'full',
            // Only the card is header: this root also spans the inset gutter
            // around it and, while auto-hidden, the strip the card has slid out
            // of. Neither may swallow clicks meant for the page — or for the
            // flyout backdrop, which is fixed over the viewport but a child of
            // this root and so hit-tests behind it. `card` takes the pointer
            // back for the header surface, the backdrop re-arms itself on open.
            pointerEvents: 'none',
            // Card insets: the bar is full-bleed on mobile (0) and from `s` up
            // sits as a card inset by `spacing-20` on every side.
            '--app-header-offset-inline': {
                base: '0px',
                s: 'spacing.spacing-20'
            },
            // The block inset comes from the global chain
            // (app/leap/theme/global-css.js), which reserves the card's full
            // footprint — bar, borders and this inset — for the page below.
            '--app-header-offset-block': 'var(--header-card-offset-block, 0px)',
            '--container-inline-padding': {
                base: 'spacing.container-padding-mobile',
                s: 'spacing.container-padding-desktop'
            },
            // The header's content edge inside the card, from `s` up. On the
            // root rather than the bar because the flyout's panes start on the
            // same line and two derivations of it would drift. Stated directly,
            // not off the module gutter: the design draws 72px inside a card
            // already inset by `--app-header-offset-inline`, which puts header
            // content 2px inboard of the gutter rather than on it (STOK-17520).
            '--desktop-inline-padding': '{spacing.spacing-72}',
            // Card border width, taken from the global chain because the
            // published footprint counts it too.
            '--app-header-card-border-width': 'var(--header-card-border-width, 0px)',
            // The header's footprint in normal flow, with the flyout closed: the
            // whole card box — bar, the card's own borders and the container's
            // block insets — and nothing the open flyout adds. It is the value
            // the header publishes, so the reservation and the rendered card
            // cannot drift apart.
            '--app-header-flow-block-size': 'var(--xleap-header-bar-block-size)',
            // How far this sticky root's top edge sits below the top of the
            // viewport: the message bar, the only thing above the header card.
            // The flyout reads it (`flyout/index.recipe.js`) to size itself
            // against the space actually left below the bar, and the search
            // overlay to place its mobile field.
            //
            // The resting value, correct at the top of the page — what SSR, the
            // first paint and a no-JS session get. The message bar scrolls away
            // while this root stays pinned, so `index.jsx` writes the measured
            // distance over it while either is open (STOK-17548, STOK-17653).
            [HEADER_VIEWPORT_OFFSET_VAR]: 'var(--header-message-bar-block-size)'
            // The root itself NEVER carries a transform, in any state. Two
            // reasons: (1) any non-`none` transform — even the identity
            // `translateY(0)` — would make the header the containing block for
            // its `position: fixed` flyout backdrop, clamping it to the header
            // box instead of the viewport. (2) In the floating variant the
            // `cardContainer`'s
            // negative `marginBlockEnd` collapses the root's own computed height
            // to ~0 (that is how the header reserves no layout space), so a
            // root-level `translateY(-100%)` would resolve to zero and the
            // auto-hide slide would do nothing. The slide therefore lives on the
            // `card`, whose own border-box height is the full rendered header
            // surface regardless of that negative margin.
        },
        // Layout-transparent so wrapping the header content in the focus-lock
        // container changes nothing about the sticky/flow behavior of `root`.
        focusTrap: {
            display: 'contents'
        },
        cardContainer: {
            // Floating only: pull the following content up by the container's own
            // height (-100%) so the floating pill reserves NO layout space and the
            // page scrolls behind it. Zero inline, where the header sits in flow,
            // and zero below `s`, where floating is not granted.
            marginBlockEnd: `calc(-100% * var(${HEADER_FLOATING_ACTIVE_VAR}, 0))`,
            // Fix the container to the closed header height so the flyout, which
            // grows the card it lives in (`flyout/index.recipe.js` animates its
            // reveal from `0fr` to `1fr`), overflows this box instead of growing
            // it. Without it the inline variant — where nothing collapses the
            // header out of flow — pushes the page content down by the open
            // panel's height and, while scrolled, lets scroll anchoring drift
            // the page on every open/close cycle (STOK-17412). The open flyout
            // overlaps the page in both variants; the card is free to overflow
            // because neither this container nor the sticky root clips.
            blockSize: 'var(--app-header-flow-block-size)',
            paddingBlock: 'var(--app-header-offset-block)',
            paddingInline: 'var(--app-header-offset-inline)'
        },
        card: {
            position: 'relative',
            // The only part of the sticky root that takes the pointer back —
            // and with it the bar and the open flyout (see `root`).
            pointerEvents: 'auto',
            // Clips the block axis so the bar looks like it stays put while the
            // reveal grows the card from behind it. The inline axis stays
            // visible so the flyout's scrollport can bleed past this card to the
            // viewport edge (`flyout/index.recipe.js`, STOK-17181); the
            // scrollport's own `overflow-x: hidden` is what keeps its content in.
            overflowX: 'visible',
            overflowY: 'clip',
            // Auto-hide slide. Keyed off the `data-hidden` attribute the
            // component sets on the sticky root (an ancestor of the card), so the
            // backdrop — a sibling of the card's container, outside its
            // transformed box — stays anchored to the viewport.
            transitionProperty: 'transform',
            // DESIGN-ISSUE(STOK-17031): auto-hide motion spec is unconfirmed —
            // the `cubic-bezier(0.5, 0, 0, 1)` easing and 0.5s duration come
            // only from Figma annotation cards, not motion metadata, and await
            // design confirmation under STOK-17031. Duration maps to the
            // `slowest` (500ms) token; no easing token matches the curve yet.
            transitionDuration: 'slowest',
            transitionTimingFunction: 'cubic-bezier(0.5, 0, 0, 1)',
            // Ancestor selector, not the self-scoped `_hidden` condition: the
            // component sets `data-hidden` on the sticky root (its forwarded ref /
            // measurement anchor), and `_hidden` could only match the attribute on
            // this element itself.
            '[data-hidden] &': {
                // The card's own height plus the container's top inset, so the
                // whole surface clears the viewport. It resolves against the card
                // rather than its fixed-height container because the card is the
                // box that grows with the flyout: hiding re-arms the moment the
                // flyout closes, while its reveal is still collapsing, and a slide
                // measured on the container would leave the tail of that
                // collapsing panel on screen.
                transform: 'translateY(calc(-100% - var(--app-header-offset-block)))'
            },
            // The card is the header's opaque surface in BOTH variants: a
            // full-bleed band inline, a rounded pill when floating. Its opacity
            // is what hides the flyout backdrop behind the header (the backdrop
            // sits a layer below, at `zIndex: hide`), so the header never looks
            // dimmed while the nav is open.
            //
            // Mobile hands that job to `bar` and to the flyout `panel`
            // separately, because the flyout fades in there rather than
            // expanding a canvas (see the flyout recipe): painted on the card,
            // the surface would appear full-screen the instant the panel's grid
            // row opened, ahead of the fade it is supposed to be revealing.
            // Between the two, every pixel of the card is still covered in both
            // states, so nothing about the closed header changes.
            bg: {base: 'transparent', s: 'primary.base.white'},
            // Rounded only when floating, and only from `s` up (where the card is
            // inset); inline stays a square, full-bleed band. The floating card
            // sits flat on white per the Figma spec — no drop shadow.
            borderRadius: {
                base: '0px',
                s: 'l'
            },
            borderColor: 'border.subtle',
            borderStyle: 'solid',
            borderWidth: 'var(--app-header-card-border-width)',
            // Raised above the fixed backdrop, which lives in the same sticky
            // header stacking context.
            zIndex: 'docked'
        },
        bar: {
            position: 'relative',
            zIndex: 'docked',
            width: 'full',
            // The header's opaque surface on mobile, where the card is
            // transparent so the flyout below can fade in over the backdrop. The
            // bar spans the card's full width and the card clips it, so the band
            // it paints covers exactly what the card covers on desktop.
            bg: {base: 'primary.base.white', s: 'transparent'},
            // Mobile follows the asymmetric header gutters from the design. From
            // `s` up the content edge comes from `--desktop-inline-padding` on
            // the root, which the flyout reads as well; the inner
            // `container.page` box then caps and centres it.
            paddingInlineStart: {
                base: 'spacing-24',
                s: 'var(--desktop-inline-padding)'
            },
            paddingInlineEnd: {
                base: 'spacing-20',
                s: 'var(--desktop-inline-padding)'
            }
        },
        barInner: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            // Read from the reservation (app/leap/theme/global-css.js) rather
            // than restated: the flyout is placed at this edge and sized from
            // that reservation (STOK-17509).
            height: 'var(--header-bar-content-block-size)',
            gap: 'spacing-40',
            // 11px: what the height above leaves around the 42px the row asks
            // for — `mobileSearchTrigger` where it shows, and the clearance
            // the icon buttons' focus ring needs, which paints outside their 36px box.
            paddingBlock: 'spacing-11'
        },
        // The mobile search field is absolutely positioned over the logo here,
        // so their cross-fade never temporarily lays both controls out in the row.
        logoSearch: {
            position: 'relative',
            flexShrink: 0
        },
        logo: {
            display: {base: 'inline-flex', s: 'inline-flex'},
            flexShrink: 0,
            opacity: {base: 1, s: 1},
            ...CTA_REVEAL_TRANSITION,
            // The shared Link size recipe targets nested SVGs with a selector of
            // equal specificity, so this doubles the local class to retain the
            // wordmark's 69px × 14px dimensions.
            '&&': {
                _icon: {
                    width: '69px',
                    height: '14px',
                    flexShrink: 0
                }
            },
            // 1px inset so the 14px logo art sits inside the 16px logo box from
            // the STOK-17031 mock (14px + 1px top/bottom = 16px). Block axis only:
            // the shared link recipe (`app/leap/theme/recipes/link.js`) trades its
            // 2px inline margin for 2px inline padding on focus so the link's
            // footprint stays constant, and an inline padding here would win that
            // swap and let focusing the logo move the rest of the bar (STOK-17337).
            paddingBlock: 'spacing-1'
        },
        nav: {
            display: {base: 'none', s: 'flex'},
            alignItems: 'center',
            gap: 'spacing-24'
        },
        navLinkText: {
            '& span::after': {
                display: 'none'
            }
        },
        divider: {
            // Vertical rule between the `Products` main link and the CMS nav
            // links, vertically centered in the nav row (the parent `nav` slot
            // is `alignItems: center`).
            height: 'spacing-11',
            width: 'spacing-1',
            backgroundColor: 'border.medium'
        },
        ctas: {
            display: 'flex',
            alignItems: 'center',
            gap: 'spacing-8',
            // Push the cart/account cluster to the far right so the logo and
            // nav links stay as a left cluster (bar is `flex-start`).
            marginInlineStart: 'auto'
        },
        menuButton: {
            display: {base: 'inline-flex', s: 'none'}
        },
        desktopCta: {
            // The closed mobile bar shows only cart and the sandwich menu, so
            // wishlist and account are hidden below the `s` breakpoint. They
            // stay mounted (display toggled) to mirror the menuButton slot.
            display: {base: 'none', s: 'inline-flex'},
            // Resting state of the mobile reveal (see the `open` variant).
            // Desktop shows these CTAs in both flyout states, so it pins the
            // settled opacity and never animates.
            opacity: {base: 0, s: 1},
            ...CTA_REVEAL_TRANSITION
        },
        mobileAccountCta: {
            display: 'none',
            opacity: 0,
            ...CTA_REVEAL_TRANSITION
        }
    },
    variants: {
        /**
         * Flyout open state. On mobile it reveals wishlist and account beside
         * the search trigger, cart, and close toggle; desktop shows its CTAs in
         * either state.
         *
         * On mobile the account and wishlist CTAs cross-fade with the flyout rather
         * than snapping (STOK-17405). `_starting` (`@starting-style`) supplies
         * the entry opacity — an element coming back from `display: none` has no
         * previous style to transition from, so without it the fade-in would not
         * run at all. Closing needs no `@starting-style`: the box is still
         * rendered, so the reverse transition has a value to start from.
         *
         * Browsers without `@starting-style` / `transition-behavior` ignore both
         * and fall back to the instant swap this replaces.
         */
        open: {
            true: {
                logo: {
                    display: {base: 'none', s: 'inline-flex'},
                    opacity: {base: 0, s: 1}
                },
                desktopCta: {
                    display: 'inline-flex',
                    opacity: 1,
                    _starting: {
                        opacity: 0
                    }
                },
                mobileAccountCta: {
                    display: {base: 'inline-flex', s: 'none'},
                    opacity: 1,
                    _starting: {
                        opacity: 0
                    }
                }
            },
            false: {
                logo: {
                    display: 'inline-flex',
                    opacity: '1'
                }
            }
        },
        // The desktop search overlay portals into `card` to inherit its actual
        // width and position. Its results extend below the bar, so the card
        // cannot clip them while it is replacing the header.
        searchOpen: {
            true: {
                card: {
                    // Longhands, not the shorthand: the base states both axes.
                    overflowX: 'visible',
                    overflowY: 'visible'
                }
            },
            false: {}
        }
    },
    defaultVariants: {
        open: false
    }
})

/**
 * Bridge map from a header link's `special` flag to the shared Link recipe
 * variant (`app/leap/theme/recipes/link.js`): a `special` navigation item uses
 * the brand `primary` variant, a regular link the `secondary` variant
 * (STOK-17031 Step 12, see `docs/agents/navigation-tree.md`). Keyed by the
 * boolean coerced to a string so the call site stays a plain map lookup rather
 * than a styling ternary (see `docs/agents/recipes.md`).
 */
export const HEADER_LINK_VARIANT = {
    true: 'primary',
    false: 'secondary'
}
